import { useCurrency, Money, GbpNote } from "../lib/currency";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import EscrowTimeline from "../components/EscrowTimeline";
import StarRating from "../components/StarRating";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Wallet, ShieldAlert, Send, ExternalLink, Star, Sparkles, TrendingUp, RefreshCw,
} from "lucide-react";
import HireStudioPanel from "../components/editor/HireStudioPanel";
import AcademyBadges from "../components/AcademyBadges";
import { DeliverableUploader, DeliverableGallery } from "../components/DeliverableUploader";
import { DeliverableReview } from "../components/DeliverableReview";
import { PerformancePanel } from "../components/PerformancePanel";
import { ShippingPanel } from "../components/ShippingPanel";
import { useDraft } from "../lib/useDraft";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbsolute = (url) => (!url || url.startsWith("http") ? url : `${BACKEND_URL}${url}`);

function StatusPill({ status }) {
  const map = {
    pending: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
    in_progress: "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30",
    delivered: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
    released: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    partially_released: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    refunded: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    disputed: "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30",
    admin_review: "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${map[status] || map.pending}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function EvidencePanel({ evidence, note, onNote, url, onUrl, onSubmit, busy }) {
  return (
    <div className="mt-4 p-4 rounded-xl bg-white/[0.02] hairline space-y-3" data-testid="evidence-panel">
      <div className="text-xs uppercase tracking-widest text-zinc-400">Evidence timeline</div>
      {evidence.length === 0 ? (
        <p className="text-sm text-zinc-500">No evidence submitted yet. Add screenshots links, message references, or a short factual account of what happened.</p>
      ) : (
        <ol className="space-y-2" data-testid="evidence-list">
          {evidence.map((e) => (
            <li key={e.id} className="p-3 rounded-lg bg-black/30 border border-white/5" data-testid={`evidence-${e.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-zinc-300">
                  <span className={`font-semibold ${e.author_role === "brand" ? "text-nexus-blue" : "text-nexus-pink"}`}>{e.author_name}</span>{" "}
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{e.author_role}</span>
                </div>
                <div className="text-[10px] text-zinc-600">{new Date(e.created_at).toLocaleString("en-GB")}</div>
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{e.note}</p>
              {e.url && (
                <a href={e.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs text-nexus-blue hover:underline">
                  <ExternalLink className="h-3 w-3" /> {e.url.length > 60 ? e.url.slice(0, 60) + "…" : e.url}
                </a>
              )}
            </li>
          ))}
        </ol>
      )}
      <div className="pt-2 border-t border-white/5 space-y-2">
        <Textarea
          value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder="Add your side — what happened, what was agreed, any timestamps…"
          className="bg-white/[0.02] border-white/10 text-white min-h-[80px]"
          data-testid="evidence-note-input"
        />
        <Input
          value={url}
          onChange={(e) => onUrl(e.target.value)}
          placeholder="Optional link (screenshot, drive, screen recording)"
          className="bg-white/[0.02] border-white/10 text-white"
          data-testid="evidence-url-input"
        />
        <Button
          onClick={onSubmit}
          disabled={busy || !note.trim()}
          className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 w-full h-10"
          data-testid="evidence-submit-btn"
        >
          Submit evidence
        </Button>
      </div>
    </div>
  );
}

function RatingSelector({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5">{label}</div>
      <div className="flex gap-1" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="p-1"
            data-testid={`${testid}-${n}`}
          >
            <Star
              className={`h-6 w-6 ${n <= value ? "text-[#FFEA00] fill-[#FFEA00]" : "text-zinc-700"}`}
              strokeWidth={1.5}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DealRoom() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hire, setHire] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const chatDraft = useDraft(user ? `chat:${id}` : null, { value: msgText, isEmpty: (v) => !v?.trim(), onRestore: (v) => setMsgText(v || "") });
  const [deliverUrl, setDeliverUrl] = useState("");
  const [deliverNote, setDeliverNote] = useState("");
  const [deliverFiles, setDeliverFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [caseStudy, setCaseStudy] = useState(null);
  const [publishingCS, setPublishingCS] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [partialPct, setPartialPct] = useState(50);
  const [partialReason, setPartialReason] = useState("");
  const [evidence, setEvidence] = useState([]);
  const [evidenceNote, setEvidenceNote] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [evidenceBusy, setEvidenceBusy] = useState(false);
  const [review, setReview] = useState({
    quality: 5, communication: 5, creativity: 5, delivery_speed: 5, professionalism: 5, comment: "",
  });
  const msgEndRef = useRef(null);

  const loadAll = async () => {
    try {
      const [h, m, cs, dn] = await Promise.all([
        api.get(`/hires/${id}`),
        api.get(`/hires/${id}/messages`),
        api.get(`/hires/${id}/case-study`).catch(() => ({ data: null })),
        api.get(`/hires/${id}/deliverable-notes`).catch(() => ({ data: { notes: [] } })),
      ]);
      setHire(h.data);
      setMessages(m.data);
      setNotes(dn.data?.notes || []);
      setCaseStudy(cs.data && cs.data.slug ? cs.data : null);
      if (["disputed", "admin_review", "partially_released", "refunded"].includes(h.data.status)) {
        api.get(`/hires/${id}/dispute-evidence`).then((r) => setEvidence(r.data?.items || [])).catch(() => setEvidence([]));
      } else {
        setEvidence([]);
      }
    } catch (e) {
      toast.error("Failed to load deal");
    }
  };

  const submitEvidence = async () => {
    if (!evidenceNote.trim()) { toast.error("Add a short note describing your evidence"); return; }
    setEvidenceBusy(true);
    try {
      await api.post(`/hires/${id}/dispute-evidence`, { note: evidenceNote.trim(), url: evidenceUrl.trim() || null });
      setEvidenceNote("");
      setEvidenceUrl("");
      const r = await api.get(`/hires/${id}/dispute-evidence`);
      setEvidence(r.data?.items || []);
      toast.success("Evidence submitted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setEvidenceBusy(false); }
  };

  const publishCaseStudy = async () => {
    setPublishingCS(true);
    try {
      const { data } = await api.post(`/case-studies/from-hire/${id}`);
      setCaseStudy(data);
      toast.success("Case study published — share the link");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPublishingCS(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth?mode=login"); return; }
    loadAll();
    const t = setInterval(() => {
      api.get(`/hires/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id, user, loading]);

  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    if (!user || loading) return;
    const sessionId = searchParams.get("session_id");
    if (searchParams.get("payment") === "cancelled") {
      toast.error("Payment cancelled — no charge was made");
      setSearchParams({}, { replace: true });
      return;
    }
    if (!sessionId) return;
    let alive = true;
    let tries = 0;
    setConfirmingPayment(true);
    const poll = async () => {
      try {
        const r = await api.get(`/hires/${id}/fund/status/${sessionId}`);
        if (!alive) return;
        if (r.data.payment_status === "paid") {
          toast.success("Escrow funded — the creator can start");
          await loadAll();
          setConfirmingPayment(false);
          setSearchParams({}, { replace: true });
        } else if (r.data.payment_status === "expired") {
          toast.error("Checkout expired — try again");
          setConfirmingPayment(false);
          setSearchParams({}, { replace: true });
        } else if (tries++ < 15) {
          setTimeout(poll, 2000);
        } else {
          setConfirmingPayment(false);
          toast.error("Still confirming with Stripe — refresh in a moment");
        }
      } catch {
        if (alive) { setConfirmingPayment(false); setSearchParams({}, { replace: true }); }
      }
    };
    poll();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [user, loading, id]);

  const call = async (fn, opts = {}) => {
    setBusy(true);
    try {
      await fn();
      await loadAll();
      opts.success && toast.success(opts.success);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const fund = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hires/${id}/fund`, { origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Could not start checkout");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const deliver = () => call(() => api.post(`/hires/${id}/deliver`, { deliverable_url: deliverUrl, note: deliverNote, files: deliverFiles }), { success: "Delivery submitted — the brand has been notified" });
  const approve = () => call(() => api.post(`/hires/${id}/approve`), { success: "Payment released" });
  const dispute = () => call(() => api.post(`/hires/${id}/dispute`, { reason: disputeReason }), { success: "Dispute opened" });
  const requestRevision = () => call(() => api.post(`/hires/${id}/request-revision`, { note: revisionNote }), { success: "Revision requested" });
  const partialRelease = () => call(() => api.post(`/hires/${id}/partial-release`, { percent: partialPct, reason: partialReason }), { success: `${partialPct}% released, ${100 - partialPct}% refunded` });
  const escalate = () => call(() => api.post(`/hires/${id}/escalate`), { success: "Escalated to Nexus admin review" });
  const submitReview = () => call(() => api.post(`/hires/${id}/review`, review), { success: "Thanks for your review" });

  const addNote = async (payload) => {
    try {
      const { data } = await api.post(`/hires/${id}/deliverable-notes`, payload);
      setNotes((n) => [...n, data]);
      toast.success(`Pinned at ${data.timestamp ? Math.floor(data.timestamp / 60) + ":" + String(Math.floor(data.timestamp % 60)).padStart(2, "0") : "0:00"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const resolveNote = async (n) => {
    try {
      await api.patch(`/hires/${id}/deliverable-notes/${n.id}/resolve`);
      setNotes((arr) => arr.map((x) => (x.id === n.id ? { ...x, resolved: true } : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const deleteNote = async (n) => {
    try {
      await api.delete(`/hires/${id}/deliverable-notes/${n.id}`);
      setNotes((arr) => arr.filter((x) => x.id !== n.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const openNotes = notes.filter((n) => n.revision_round == null);
  const lastRoundNotes = notes.filter((n) => n.revision_round === hire?.revision_count);

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    try {
      const { data } = await api.post(`/hires/${id}/messages`, { text: msgText });
      setMessages((m) => [...m, data]);
      setMsgText(""); chatDraft.clear();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!hire || !user) return (
    <div className="min-h-screen grain"><Navbar /><div className="max-w-6xl mx-auto p-10 text-zinc-500 animate-pulse">Loading…</div></div>
  );

  const isBrand = user.role === "brand" && hire.brand_id === user.id;
  const isCreator = user.role === "creator" && hire.creator_id === user.id;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback={isBrand ? "/brand" : "/creator"} label="Back to dashboard" />
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-10 pt-4 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Deal room</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">
              {hire.package?.title}
            </h1>
            <p className="text-zinc-400 mt-1">
              {isBrand ? `with ${hire.creator_name}` : `for ${hire.brand_name}`}
            </p>
            {hire.creator_id && (
              <div className="mt-3">
                <AcademyBadges userId={hire.creator_id} compact={true} title={isBrand ? "Creator's Academy" : "Your Academy"} />
              </div>
            )}
          </div>
          <StatusPill status={hire.status} />
        </div>

        <div className="glass rounded-2xl p-8 md:p-10 relative">
          <EscrowTimeline status={hire.status} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6 min-w-0">
          {/* Main col */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Brief */}
            <section className="p-6 rounded-2xl bg-[#0C0C10] hairline">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Brief</div>
              <p className="text-zinc-200 whitespace-pre-wrap">{hire.brief || <em className="text-zinc-500">No brief provided.</em>}</p>
            </section>

            {/* Studio drafts + timestamped brand feedback — visible from funded onwards */}
            {["funded", "in_progress", "delivered", "released", "partially_released", "disputed", "admin_review"].includes(hire.status) && (
              <HireStudioPanel
                hireId={hire.id}
                role={isBrand ? "brand" : "creator"}
                hire={hire}
              />
            )}

            {/* Performance bonus tracking */}
            {hire.performance && <PerformancePanel hire={hire} isBrand={isBrand} />}

            {/* Product shipping + parcel tracking */}
            <ShippingPanel hire={hire} isBrand={isBrand} isCreator={isCreator} user={user} onChange={loadAll} />

            {/* Actions */}
            {isBrand && hire.status === "pending" && (
              <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="brand-fund-panel">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Next step</div>
                <h3 className="font-display text-xl">Fund escrow to start</h3>
                <p className="text-sm text-zinc-400">
                  You'll pay <Money gbp={hire.brand_total} /> securely via Stripe. Nexus holds it in escrow and the creator only gets paid
                  once you approve delivered content. The job only becomes active once payment is confirmed.
                </p>
                {confirmingPayment ? (
                  <div className="text-sm text-nexus-blue animate-pulse" data-testid="escrow-confirming">Confirming your payment with Stripe…</div>
                ) : (
                  <Button onClick={fund} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="fund-escrow-btn">
                    <Wallet className="h-4 w-4 mr-2" /> Pay & fund escrow · <Money gbp={hire.brand_total} />
                  </Button>
                )}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Charged in GBP · Secured by Stripe</div>
              </section>
            )}

            {isCreator && hire.status === "in_progress" && (
              <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="creator-deliver-panel">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Next step</div>
                <h3 className="font-display text-xl">Submit your video for approval</h3>
                <p className="text-sm text-zinc-400">Upload the finished video so {hire.brand_name} can watch it here and approve. Payment is released to you once they confirm.</p>
                <DeliverableUploader files={deliverFiles} onChange={setDeliverFiles} disabled={busy} />
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Link (optional)</Label>
                  <Input value={deliverUrl} onChange={(e) => setDeliverUrl(e.target.value)} placeholder="Google Drive / Instagram post URL" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" data-testid="deliver-url-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Note</Label>
                  <Textarea value={deliverNote} onChange={(e) => setDeliverNote(e.target.value)} rows={3} placeholder="Anything the brand should know…" className="mt-1.5 bg-white/[0.02] border-white/10 text-white" />
                </div>
                <Button onClick={deliver} disabled={busy || (!deliverUrl.trim() && deliverFiles.length === 0)} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="submit-delivery-btn">
                  Send for brand approval
                </Button>
              </section>
            )}

            {hire.status === "delivered" && (
              <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="delivered-panel">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Delivered</div>
                <h3 className="font-display text-xl">{isBrand ? "Review the video and approve" : "Awaiting brand approval"}</h3>
                {isBrand && hire.deliverable_files?.length > 0 && (
                  <p className="text-sm text-zinc-400">Pause the video at any moment and pin a note — e.g. <em>"0:30 — add the logo here"</em>. Pinned notes are sent to the creator when you request a revision.</p>
                )}
                <DeliverableReview
                  files={hire.deliverable_files}
                  notes={openNotes}
                  toAbsolute={toAbsolute}
                  role={isBrand ? "brand" : "creator"}
                  canPin={isBrand}
                  onPin={addNote}
                  onResolve={resolveNote}
                  onDelete={deleteNote}
                  userId={user.id}
                />
                {hire.deliverable_url && (
                  <a href={hire.deliverable_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-nexus-blue hover:underline" data-testid="deliverable-link">
                    <ExternalLink className="h-4 w-4" /> {hire.deliverable_url}
                  </a>
                )}
                {hire.delivery_note && <p className="text-sm text-zinc-300 whitespace-pre-wrap">{hire.delivery_note}</p>}
                {isBrand && (
                  <div className="space-y-4">
                    <div className="flex gap-3 flex-wrap">
                      <Button onClick={approve} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="approve-btn">
                        Approve & release · <Money gbp={hire.creator_payout} /> to creator
                      </Button>
                    </div>
                    <div className="border-t border-white/5 pt-4 space-y-3">
                      <div className="text-xs uppercase tracking-widest text-zinc-500">Not quite right?</div>
                      {openNotes.length > 0 && (
                        <div className="text-xs text-nexus-pink" data-testid="pinned-notes-count">
                          {openNotes.length} pinned note{openNotes.length > 1 ? "s" : ""} will be sent with this revision request.
                        </div>
                      )}
                      <Textarea
                        value={revisionNote}
                        onChange={(e) => setRevisionNote(e.target.value)}
                        rows={2}
                        placeholder={openNotes.length > 0 ? "Optional overall note (your pinned notes are included automatically)" : "What needs to change? (revision brief)"}
                        className="bg-white/[0.02] border-white/10 text-white"
                        data-testid="revision-note-input"
                      />
                      <div className="flex gap-3 flex-wrap">
                        <Button
                          variant="outline"
                          onClick={requestRevision}
                          disabled={busy || (!revisionNote.trim() && openNotes.length === 0)}
                          className="bg-white/5 border-white/15 text-white hover:bg-white/10 hover:text-white rounded-full"
                          data-testid="request-revision-btn"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" /> Request revision
                        </Button>
                        <Button
                          variant="outline"
                          onClick={dispute}
                          disabled={busy}
                          className="bg-white/5 border-nexus-pink/40 text-nexus-pink hover:bg-nexus-pink/10 hover:text-nexus-pink rounded-full"
                          data-testid="dispute-btn"
                        >
                          <ShieldAlert className="h-4 w-4 mr-2" /> Open dispute
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {hire.revision_count > 0 && (
                  <div className="text-xs text-zinc-500">
                    Revision {hire.revision_count} · previous note: <span className="text-zinc-300">{hire.revision_note}</span>
                  </div>
                )}
              </section>
            )}

            {hire.status === "in_progress" && hire.revision_count > 0 && isCreator && (
              <section className="p-6 rounded-2xl bg-yellow-500/5 border border-yellow-500/20 space-y-4" data-testid="revision-panel">
                <div className="text-xs uppercase tracking-widest text-yellow-300">Revision requested</div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{hire.revision_note || "Please review and adjust the delivery."}</p>
                {hire.previous_deliverable_files?.length > 0 && (
                  <div className="space-y-2" data-testid="revision-previous-cut">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Your previous cut · click a timestamp to jump to it</div>
                    <DeliverableReview
                      files={hire.previous_deliverable_files}
                      notes={lastRoundNotes}
                      toAbsolute={toAbsolute}
                      role="creator"
                      canPin={false}
                      onResolve={resolveNote}
                      userId={user.id}
                    />
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Revision round {hire.revision_count}</div>
              </section>
            )}

            {hire.status === "disputed" && (
              <section className="p-6 rounded-2xl bg-nexus-pink/5 border border-nexus-pink/20 space-y-4" data-testid="disputed-panel">
                <div className="text-xs uppercase tracking-widest text-nexus-pink">Dispute open</div>
                <h3 className="font-display text-xl">Try to settle it — or escalate to Nexus</h3>
                {hire.dispute_reason && (
                  <p className="text-sm text-zinc-200">Reason: <span className="text-zinc-100">{hire.dispute_reason}</span></p>
                )}
                {isBrand && (
                  <div className="space-y-3 p-4 rounded-xl bg-white/[0.02] hairline">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Offer a partial release</div>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="10"
                        max="90"
                        step="5"
                        value={partialPct}
                        onChange={(e) => setPartialPct(parseInt(e.target.value))}
                        className="flex-1 accent-nexus-pink"
                        data-testid="partial-range"
                      />
                      <div className="font-display font-bold text-lg tabular-nums w-14 text-right nexus-gradient-text">{partialPct}%</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-emerald-200">
                        To creator: <b><Money gbp={hire.amount * partialPct / 100} /></b>
                      </div>
                      <div className="p-2 rounded-lg bg-purple-500/5 border border-purple-500/20 text-purple-200">
                        Refund you: <b><Money gbp={hire.amount * (100 - partialPct) / 100} /></b>
                      </div>
                    </div>
                    <Input
                      value={partialReason}
                      onChange={(e) => setPartialReason(e.target.value)}
                      placeholder="Reason (visible to creator)"
                      className="bg-white/[0.02] border-white/10 text-white"
                      data-testid="partial-reason-input"
                    />
                    <Button
                      onClick={partialRelease}
                      disabled={busy}
                      className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 w-full h-10"
                      data-testid="partial-release-btn"
                    >
                      Settle · {partialPct}% to creator
                    </Button>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={escalate}
                  disabled={busy}
                  className="bg-white/5 border-nexus-pink/40 text-nexus-pink hover:bg-nexus-pink/10 hover:text-nexus-pink rounded-full"
                  data-testid="escalate-btn"
                >
                  <ShieldAlert className="h-4 w-4 mr-2" /> Escalate to Nexus admin review
                </Button>
                <EvidencePanel
                  evidence={evidence}
                  note={evidenceNote}
                  onNote={setEvidenceNote}
                  url={evidenceUrl}
                  onUrl={setEvidenceUrl}
                  onSubmit={submitEvidence}
                  busy={evidenceBusy}
                />
              </section>
            )}

            {hire.status === "admin_review" && (
              <section className="p-6 rounded-2xl bg-nexus-pink/5 border border-nexus-pink/20 space-y-4" data-testid="admin-review-panel">
                <div className="text-xs uppercase tracking-widest text-nexus-pink">Under admin review</div>
                <h3 className="font-display text-xl">Nexus is reviewing this dispute</h3>
                <p className="text-sm text-zinc-300">
                  Our team is looking into the deliverable, brief and both sides of the chat. Expect a decision within 48h — we&apos;ll notify you here and by push.
                </p>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Escalated {hire.escalated_at ? new Date(hire.escalated_at).toLocaleDateString() : ""}
                </div>
                <EvidencePanel
                  evidence={evidence}
                  note={evidenceNote}
                  onNote={setEvidenceNote}
                  url={evidenceUrl}
                  onUrl={setEvidenceUrl}
                  onSubmit={submitEvidence}
                  busy={evidenceBusy}
                />
              </section>
            )}

            {hire.status === "partially_released" && (
              <section className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 space-y-2" data-testid="partial-released-panel">
                <div className="text-xs uppercase tracking-widest text-emerald-300">Settled — partial release</div>
                <h3 className="font-display text-xl">
                  {hire.partial_percent}% released · {100 - (hire.partial_percent || 0)}% refunded
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-white/[0.02] hairline">
                    <div className="text-xs text-zinc-500">To creator</div>
                    <div className="font-display font-bold text-lg text-emerald-300"><Money gbp={hire.creator_payout || 0} /></div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/[0.02] hairline">
                    <div className="text-xs text-zinc-500">Refunded to brand</div>
                    <div className="font-display font-bold text-lg text-purple-300"><Money gbp={hire.refund_amount || 0} /></div>
                  </div>
                </div>
                {hire.partial_reason && (
                  <p className="text-xs text-zinc-400 pt-2">Reason: <span className="text-zinc-300">{hire.partial_reason}</span></p>
                )}
              </section>
            )}

            {["released", "partially_released"].includes(hire.status) && hire.deliverable_files?.length > 0 && (
              <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="approved-deliverable-panel">
                <div className="text-xs uppercase tracking-widest text-emerald-300">Approved video</div>
                <DeliverableGallery files={hire.deliverable_files} toAbsolute={toAbsolute} />
              </section>
            )}


            {hire.status === "released" && isBrand && (
              <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="review-panel">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Final step</div>
                <h3 className="font-display text-xl">Leave a review</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <RatingSelector label="Quality" value={review.quality} onChange={(v) => setReview({ ...review, quality: v })} testid="rating-quality" />
                  <RatingSelector label="Communication" value={review.communication} onChange={(v) => setReview({ ...review, communication: v })} testid="rating-communication" />
                  <RatingSelector label="Creativity" value={review.creativity} onChange={(v) => setReview({ ...review, creativity: v })} testid="rating-creativity" />
                  <RatingSelector label="Delivery speed" value={review.delivery_speed} onChange={(v) => setReview({ ...review, delivery_speed: v })} testid="rating-delivery" />
                  <RatingSelector label="Professionalism" value={review.professionalism} onChange={(v) => setReview({ ...review, professionalism: v })} testid="rating-professionalism" />
                </div>
                <Textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} rows={3} placeholder="Public comment (optional)" className="bg-white/[0.02] border-white/10 text-white" data-testid="review-comment-input" />
                <Button onClick={submitReview} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="submit-review-btn">
                  Submit review
                </Button>
              </section>
            )}

            {hire.status === "released" && !isBrand && (
              <section className="p-6 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 text-emerald-200" data-testid="released-note">
                <div className="text-xs uppercase tracking-widest text-emerald-400 mb-1">Paid out</div>
                <h3 className="font-display text-xl">Payment released</h3>
                <p className="text-sm"><Money gbp={hire.creator_payout} /> has been released to your payout balance.</p>
              </section>
            )}

            {hire.status === "released" && (
              <section className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-4" data-testid="case-study-panel">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-nexus-blue" />
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Share this collab</div>
                </div>
                {caseStudy ? (
                  <>
                    <h3 className="font-display text-xl">Case study is live</h3>
                    <p className="text-sm text-zinc-400">{caseStudy.title}</p>
                    <div className="flex gap-2 flex-wrap">
                      <a
                        href={`/case-studies/${caseStudy.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 nexus-gradient text-white text-xs font-semibold px-4 h-9 rounded-full hover:opacity-90"
                        data-testid="view-case-study-btn"
                      >
                        View public page <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/case-studies/${caseStudy.slug}`;
                          navigator.clipboard?.writeText(url);
                          toast.success("Link copied to clipboard");
                        }}
                        className="bg-white/5 border border-white/15 text-white text-xs font-semibold px-4 h-9 rounded-full hover:bg-white/10"
                        data-testid="copy-case-study-btn"
                      >
                        Copy shareable link
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-display text-xl">Publish as a case study</h3>
                    <p className="text-sm text-zinc-400">
                      Turn this completed collab into a public case-study page that ranks on Google and pulls new brands in. Our AI drafts the copy from the brief, review and creator profile — takes a few seconds.
                    </p>
                    <button
                      onClick={publishCaseStudy}
                      disabled={publishingCS}
                      className="inline-flex items-center gap-2 nexus-gradient text-white text-sm font-semibold px-5 h-10 rounded-full hover:opacity-90"
                      data-testid="publish-case-study-btn"
                    >
                      <Sparkles className="h-4 w-4" />
                      {publishingCS ? "Drafting…" : "Publish case study"}
                    </button>
                  </>
                )}
              </section>
            )}
          </div>

          {/* Right: summary + chat */}
          <aside className="space-y-6 min-w-0">
            <div className="p-6 rounded-2xl bg-[#0C0C10] hairline space-y-3" data-testid="deal-summary">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Escrow summary</div>
              <div className="flex justify-between text-sm"><span className="text-zinc-400">Package</span><span><Money gbp={hire.amount} /></span></div>
              {hire.bonus_cap > 0 && <div className="flex justify-between text-sm" data-testid="summary-bonus-pool"><span className="text-zinc-400">Bonus pool (escrow)</span><span><Money gbp={hire.bonus_cap} /></span></div>}
              <div className="flex justify-between text-sm"><span className="text-zinc-400">Platform fee</span><span><Money gbp={hire.platform_fee} /></span></div>
              <div className="flex justify-between font-semibold border-t border-white/10 pt-3"><span>Brand total</span><span><Money gbp={hire.brand_total} /></span></div>
              <div className="flex justify-between text-sm text-nexus-blue"><span>Creator payout</span><span><Money gbp={hire.creator_payout} /></span></div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 pt-2">
                {hire.creator_fee > 0 ? `Creator fee £${hire.creator_fee.toFixed(2)} · deducted from payout` : "Creator keeps 100% · platform fee paid by brand"}
              </div>
              <GbpNote className="pt-1" />
            </div>

            <div className="p-4 rounded-2xl bg-[#0C0C10] hairline flex flex-col h-[520px]" data-testid="deal-chat">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 px-1">Messages</div>
              <div className="flex-1 overflow-y-auto space-y-2 px-1">
                {messages.length === 0 && (
                  <div className="text-sm text-zinc-500 text-center py-10">No messages yet. Say hi.</div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "nexus-gradient text-white" : "bg-white/5 text-zinc-100 border border-white/10"}`}>
                        {!mine && <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name}</div>}
                        <div className="whitespace-pre-wrap">{m.text}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEndRef} />
              </div>
              <div className="mt-3 flex gap-2">
                <Input
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMsg())}
                  placeholder="Message…"
                  className="bg-white/[0.02] border-white/10 text-white"
                  data-testid="chat-input"
                />
                <Button onClick={sendMsg} className="nexus-gradient text-white border-0 rounded-full px-4 hover:opacity-90" data-testid="chat-send-btn">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
